import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CreditCard, Plus, Search, Edit, Trash2, Eye, X, DollarSign, TrendingUp, Building2 } from 'lucide-react';

interface BankAccount {
  id: number;
  accountName: string;
  accountNumber: string;
  bankName: string;
  branchCode?: string;
  swiftCode?: string;
  iban?: string;
  currentBalance: number;
  currency: string;
  accountType: string;
  isActive: boolean;
  isDefault: boolean;
  notes?: string;
  createdAt: string;
}

export default function BankAccountsPage() {
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<BankAccount | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<BankAccount | null>(null);
  const [form, setForm] = useState({
    accountName: '', accountNumber: '', bankName: '', branchCode: '',
    swiftCode: '', iban: '', currentBalance: '0', currency: 'EGP',
    accountType: 'Current', isActive: true, isDefault: false, notes: ''
  });

  useEffect(() => { loadAccounts(); }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/bankaccounts');
      if (res.data?.success) setAccounts(Array.isArray(res.data.data) ? res.data.data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingAccount(null);
    setForm({ accountName: '', accountNumber: '', bankName: '', branchCode: '', swiftCode: '', iban: '', currentBalance: '0', currency: 'EGP', accountType: 'Current', isActive: true, isDefault: false, notes: '' });
    setShowModal(true);
  };

  const openEdit = (a: BankAccount) => {
    setEditingAccount(a);
    setForm({
      accountName: a.accountName || '',
      accountNumber: a.accountNumber || '',
      bankName: a.bankName || '',
      branchCode: a.branchCode || '',
      swiftCode: a.swiftCode || '',
      iban: a.iban || '',
      currentBalance: a.currentBalance.toString(),
      currency: a.currency || 'EGP',
      accountType: a.accountType || 'Current',
      isActive: a.isActive,
      isDefault: a.isDefault,
      notes: a.notes || ''
    });
    setShowModal(true);
  };

  const openView = (a: BankAccount) => { setSelectedAccount(a); setShowViewModal(true); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        accountName: form.accountName,
        accountNumber: form.accountNumber,
        bankName: form.bankName,
        branchCode: form.branchCode,
        swiftCode: form.swiftCode,
        iban: form.iban,
        currentBalance: parseFloat(form.currentBalance) || 0,
        currency: form.currency,
        accountType: form.accountType,
        isActive: form.isActive,
        isDefault: form.isDefault,
        notes: form.notes
      };
      if (editingAccount) {
        await api.put('/api/bankaccounts/' + editingAccount.id, payload);
        alert("تم تحديث الحساب البنكي بنجاح");
      } else {
        await api.post('/api/bankaccounts', payload);
        alert("تم إضافة الحساب البنكي بنجاح");
      }
      setShowModal(false);
      loadAccounts();
    } catch (err: any) {
      alert(err.response?.data?.message || "حدث خطأ");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("هل أنت متأكد من حذف الحساب البنكي؟")) return;
    try {
      await api.delete('/api/bankaccounts/' + id);
      loadAccounts();
      alert("تم الحذف بنجاح");
    } catch (err: any) {
      alert(err.response?.data?.message || "حدث خطأ");
    }
  };

  const filtered = accounts.filter(a =>
    (a.accountName || '').toLowerCase().includes(search.toLowerCase()) ||
    (a.bankName || '').toLowerCase().includes(search.toLowerCase()) ||
    (a.accountNumber || '').includes(search)
  );

  const totalBalance = accounts.reduce((sum, a) => sum + (a.currentBalance || 0), 0);
  const activeAccounts = accounts.filter(a => a.isActive).length;
  const defaultAccount = accounts.find(a => a.isDefault);
  const inputClass = "w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-green-500 transition";

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
            <CreditCard className="w-6 h-6 text-white" />
          </div>
          <div>
            <span>الحسابات البنكية</span>
            <p className="text-sm text-gray-500 font-normal mt-1">إدارة الحسابات البنكية للشركة</p>
          </div>
        </h1>
        <Button onClick={openCreate} className="bg-gradient-to-l from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg">
          <Plus className="w-5 h-5 ml-2" />
          <span>إضافة حساب جديد</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center"><CreditCard className="w-5 h-5 text-green-600" /></div></div>
          <p className="text-sm text-gray-500">إجمالي الحسابات</p>
          <p className="text-2xl font-bold text-gray-900">{accounts.length}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-blue-600" /></div></div>
          <p className="text-sm text-gray-500">الحسابات النشطة</p>
          <p className="text-2xl font-bold text-blue-700">{activeAccounts}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center"><DollarSign className="w-5 h-5 text-orange-600" /></div></div>
          <p className="text-sm text-gray-500">إجمالي الأرصدة</p>
          <p className="text-2xl font-bold text-orange-700">{totalBalance.toLocaleString()} {accounts[0]?.currency || 'EGP'}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center"><Building2 className="w-5 h-5 text-purple-600" /></div></div>
          <p className="text-sm text-gray-500">الحساب الافتراضي</p>
          <p className="text-lg font-bold text-purple-700 truncate">{defaultAccount?.accountName || '-'}</p>
        </CardContent></Card>
      </div>

      <Card className="border-0 shadow-lg">
        <div className="p-6">
          <div className="relative mb-6">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" placeholder="بحث باسم الحساب أو البنك أو رقم الحساب..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-green-500 transition" />
          </div>
          {loading ? (
            <div className="p-12 text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div><p className="text-gray-500 mt-4">جاري التحميل...</p></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center"><CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" /><h3 className="text-lg font-semibold text-gray-900 mb-2">لا توجد حسابات بنكية</h3></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">اسم الحساب</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">البنك</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">رقم الحساب</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">IBAN</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">الرصيد</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">الحالة</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((account) => (
                    <tr key={account.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition">
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white"><CreditCard className="w-5 h-5" /></div>
                          <div>
                            <p className="font-semibold text-gray-900">{account.accountName}</p>
                            {account.isDefault && <span className="text-xs text-purple-600">افتراضي</span>}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-5"><span className="text-gray-700">{account.bankName}</span></td>
                      <td className="py-4 px-5"><span className="font-mono text-sm text-gray-700">{account.accountNumber}</span></td>
                      <td className="py-4 px-5"><span className="font-mono text-xs text-gray-500">{account.iban || '-'}</span></td>
                      <td className="py-4 px-5"><span className="font-bold text-green-600">{account.currentBalance.toLocaleString()} {account.currency}</span></td>
                      <td className="py-4 px-5"><span className={`px-3 py-1 rounded-full text-xs font-semibold ${account.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{account.isActive ? 'نشط' : 'غير نشط'}</span></td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openView(account)} className="p-2 hover:bg-green-50 rounded-lg text-gray-500 hover:text-green-600 transition" title="عرض"><Eye className="w-4 h-4" /></button>
                          <button onClick={() => openEdit(account)} className="p-2 hover:bg-green-50 rounded-lg text-gray-500 hover:text-green-600 transition" title="تعديل"><Edit className="w-4 h-4" /></button>
                          <button onClick={() => handleDelete(account.id)} className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition" title="حذف"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto"><div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{editingAccount ? 'تعديل الحساب البنكي' : 'إضافة حساب بنكي جديد'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>اسم الحساب *</Label><Input value={form.accountName} onChange={(e: any) => setForm({...form, accountName: e.target.value})} required className={inputClass} /></div>
                <div><Label>البنك *</Label><Input value={form.bankName} onChange={(e: any) => setForm({...form, bankName: e.target.value})} required className={inputClass} /></div>
                <div><Label>رقم الحساب *</Label><Input value={form.accountNumber} onChange={(e: any) => setForm({...form, accountNumber: e.target.value})} required className={inputClass} /></div>
                <div><Label>IBAN</Label><Input value={form.iban} onChange={(e: any) => setForm({...form, iban: e.target.value})} className={inputClass} /></div>
                <div><Label>SWIFT Code</Label><Input value={form.swiftCode} onChange={(e: any) => setForm({...form, swiftCode: e.target.value})} className={inputClass} /></div>
                <div><Label>رمز الفرع</Label><Input value={form.branchCode} onChange={(e: any) => setForm({...form, branchCode: e.target.value})} className={inputClass} /></div>
                <div><Label>الرصيد</Label><Input type="number" step="0.01" value={form.currentBalance} onChange={(e: any) => setForm({...form, currentBalance: e.target.value})} className={inputClass} /></div>
                <div><Label>العملة</Label><select value={form.currency} onChange={(e) => setForm({...form, currency: e.target.value})} className={inputClass}><option value="EGP">جنيه مصري (EGP)</option><option value="USD">دولار أمريكي (USD)</option><option value="EUR">يورو (EUR)</option><option value="SAR">ريال سعودي (SAR)</option></select></div>
                <div><Label>نوع الحساب</Label><select value={form.accountType} onChange={(e) => setForm({...form, accountType: e.target.value})} className={inputClass}><option value="Current">جاري</option><option value="Savings">توفير</option><option value="Investment">استثماري</option></select></div>
                <div className="flex items-center gap-2"><input type="checkbox" id="baActive" checked={form.isActive} onChange={(e) => setForm({...form, isActive: e.target.checked})} className="w-4 h-4" /><label htmlFor="baActive" className="text-sm font-medium">حساب نشط</label></div>
                <div className="flex items-center gap-2"><input type="checkbox" id="baDefault" checked={form.isDefault} onChange={(e) => setForm({...form, isDefault: e.target.checked})} className="w-4 h-4" /><label htmlFor="baDefault" className="text-sm font-medium">حساب افتراضي</label></div>
                <div className="md:col-span-2"><Label>ملاحظات</Label><Input value={form.notes} onChange={(e: any) => setForm({...form, notes: e.target.value})} className={inputClass} /></div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">إلغاء</Button>
                <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">{editingAccount ? 'تحديث' : 'إضافة'}</Button>
              </div>
            </form>
          </div></Card>
        </div>
      )}

      {showViewModal && selectedAccount && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl"><div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">تفاصيل الحساب البنكي</h2>
              <button onClick={() => setShowViewModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex items-center gap-4 pb-4 border-b mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white"><CreditCard className="w-8 h-8" /></div>
              <div><h3 className="text-xl font-bold">{selectedAccount.accountName}</h3><p className="text-gray-500">{selectedAccount.bankName}</p></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-sm text-gray-500">رقم الحساب</p><p className="font-semibold font-mono">{selectedAccount.accountNumber}</p></div>
              <div><p className="text-sm text-gray-500">IBAN</p><p className="font-semibold font-mono text-sm">{selectedAccount.iban || '-'}</p></div>
              <div><p className="text-sm text-gray-500">SWIFT</p><p className="font-semibold">{selectedAccount.swiftCode || '-'}</p></div>
              <div><p className="text-sm text-gray-500">رمز الفرع</p><p className="font-semibold">{selectedAccount.branchCode || '-'}</p></div>
              <div><p className="text-sm text-gray-500">الرصيد</p><p className="font-bold text-green-600 text-xl">{selectedAccount.currentBalance.toLocaleString()} {selectedAccount.currency}</p></div>
              <div><p className="text-sm text-gray-500">نوع الحساب</p><p className="font-semibold">{selectedAccount.accountType}</p></div>
              <div className="col-span-2"><p className="text-sm text-gray-500">ملاحظات</p><p className="font-semibold">{selectedAccount.notes || '-'}</p></div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button onClick={() => { setShowViewModal(false); openEdit(selectedAccount); }} className="flex-1 bg-green-600 hover:bg-green-700"><Edit className="w-4 h-4 ml-2" />تعديل</Button>
              <Button onClick={() => setShowViewModal(false)} variant="outline" className="flex-1">إغلاق</Button>
            </div>
          </div></Card>
        </div>
      )}
    </div>
  );
}
